import React, { createContext, useContext, useState, useEffect } from 'react';

const QuotaContext = createContext();

export const useQuota = () => {
  const context = useContext(QuotaContext);
  if (!context) {
    throw new Error('useQuota must be used within a QuotaProvider');
  }
  return context;
};

// Default quota values
const initialQuota = {
  amount: 50000,
  period: 'monthly',
  history: []
};

export const QuotaProvider = ({ children }) => {
  const [quota, setQuota] = useState(() => {
    const saved = localStorage.getItem('quota');
    return saved ? JSON.parse(saved) : initialQuota;
  });

  useEffect(() => {
    localStorage.setItem('quota', JSON.stringify(quota));
  }, [quota]);

  // Update quota with history tracking
  const updateQuota = (newQuotaData) => {
    setQuota(prevQuota => {
      // Save previous quota to history
      const history = [...prevQuota.history];
      if (prevQuota.amount !== newQuotaData.amount || prevQuota.period !== newQuotaData.period) {
        history.push({
          amount: prevQuota.amount,
          period: prevQuota.period,
          date: new Date().toISOString()
        });
      }

      // Only keep last 10 history items
      while (history.length > 10) {
        history.shift();
      }

      return {
        ...newQuotaData,
        history
      };
    });
  };

  // Calculate quota based on period
  const getAdjustedQuota = (period = quota.period) => {
    const baseAmount = quota.amount;
    
    // Adjust to the requested period
    switch (period) {
      case 'monthly':
        return quota.period === 'monthly' ? baseAmount :
               quota.period === 'quarterly' ? baseAmount / 3 :
               baseAmount / 12;
      case 'quarterly':
        return quota.period === 'monthly' ? baseAmount * 3 :
               quota.period === 'quarterly' ? baseAmount :
               baseAmount / 4;
      case 'yearly':
        return quota.period === 'monthly' ? baseAmount * 12 :
               quota.period === 'quarterly' ? baseAmount * 4 :
               baseAmount;
      default:
        return baseAmount;
    }
  };

  // Calculate pipeline goal (4x quota)
  const getPipelineGoal = (period = quota.period) => {
    return getAdjustedQuota(period) * 4;
  };

  return (
    <QuotaContext.Provider value={{
      quota,
      updateQuota,
      getAdjustedQuota,
      getPipelineGoal
    }}>
      {children}
    </QuotaContext.Provider>
  );
};